import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Unlock, ArrowUp, ArrowDown, Save, Search, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultPages = [
  { page_name: 'home', display_name: 'Home', route: '/', menu_order: 0 },
  { page_name: 'patient_videos', display_name: 'Patient Videos', route: '/patient-videos', menu_order: 1 },
  { page_name: 'student_resources', display_name: 'Student Resources', route: '/student-resources', menu_order: 2 },
  { page_name: 'internal_practices', display_name: 'Internal Practices', route: '/internal-practices', menu_order: 3 },
  { page_name: 'about', display_name: 'About', route: '/about', menu_order: 4 },
  { page_name: 'books', display_name: 'Books', route: '/book', menu_order: 5 },
  { page_name: 'courses', display_name: 'Courses', route: '/courses', menu_order: 6 },
  { page_name: 'class_center', display_name: 'Class Center', route: '/class-center', menu_order: 7 },
  { page_name: 'podcasts', display_name: 'Podcasts', route: '/podcasts', menu_order: 8 },
  { page_name: 'student_blog', display_name: 'Student Blog', route: '/student-blog', menu_order: 9 },
  { page_name: 'testing_center', display_name: 'Testing Center', route: '/testing-center', menu_order: 10 },
  { page_name: 'publication_center', display_name: 'Publication Center', route: '/publication-center', menu_order: 11 },
  { page_name: 'ernie_ai', display_name: 'Ernie.Ai', route: '/ernie-ai', menu_order: 12 },
  { page_name: 'study_buddy', display_name: 'Study Buddy', route: '/study-buddy', menu_order: 13 },
];

export default function PageSettingsManager() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPageSettings();
  }, []);

  const loadPageSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/page-settings`, {
        withCredentials: true
      });
      
      // Merge with defaults for any missing pages
      const existingPageNames = response.data.map(p => p.page_name);
      const missingPages = defaultPages.filter(dp => !existingPageNames.includes(dp.page_name));
      
      const allPages = [
        ...response.data.map(p => ({ ...p, is_visible: p.is_visible !== false })),
        ...missingPages.map(mp => ({ ...mp, is_visible: true, is_password_protected: false }))
      ];
      
      setPages(allPages.sort((a, b) => a.menu_order - b.menu_order));
    } catch (error) {
      console.error('Failed to load page settings:', error);
      setPages(defaultPages.map(p => ({ ...p, is_visible: true, is_password_protected: false })));
    }
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/page-settings/bulk`, pages, {
        withCredentials: true
      });
      toast.success('Page settings saved!');
      loadPageSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = (index) => {
    const updated = [...pages];
    updated[index].is_visible = !updated[index].is_visible;
    setPages(updated);
  };

  const togglePasswordProtection = (index) => {
    const updated = [...pages];
    updated[index].is_password_protected = !updated[index].is_password_protected;
    if (!updated[index].is_password_protected) {
      updated[index].password = null;
    }
    setPages(updated);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const updated = [...pages];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    // Update menu_order
    updated.forEach((page, idx) => {
      page.menu_order = idx;
    });
    setPages(updated);
  };

  const moveDown = (index) => {
    if (index === pages.length - 1) return;
    const updated = [...pages];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    // Update menu_order
    updated.forEach((page, idx) => {
      page.menu_order = idx;
    });
    setPages(updated);
  };

  const updatePageField = (index, field, value) => {
    const updated = [...pages];
    updated[index][field] = value;
    setPages(updated);
  };

  const viewPage = (route) => {
    // Open page in new tab so admin doesn't lose their place in settings
    window.open(route, '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Page Settings</h2>
        <button
          onClick={handleSaveAll}
          disabled={loading}
          className="retro-button flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Saving...' : 'Save All Changes'}</span>
        </button>
      </div>

      <div className="bg-[#E8934D]/20 border-2 border-[#E8934D] rounded-lg p-4 mb-6">
        <h3 className="font-bold text-[#2C1810] mb-2">📋 Page Management Features:</h3>
        <ul className="text-sm text-[#2C1810] space-y-1 list-disc list-inside">
          <li><strong>View Page:</strong> Preview any page (even hidden ones) - opens in new tab</li>
          <li><strong>Reorder:</strong> Use ↑ ↓ arrows to change menu order</li>
          <li><strong>Visibility:</strong> Hide pages from navigation menu</li>
          <li><strong>Password Protection:</strong> Require password to access page</li>
          <li><strong>SEO:</strong> Optimize page title, description, and keywords</li>
        </ul>
      </div>

      <div className="cassette-card p-6">
        <div className="space-y-3">
          {pages.map((page, index) => (
            <div
              key={page.page_name}
              className="p-4 bg-[#F4E4C1] rounded-lg border-2 border-[#6B4423]"
            >
              {editingPage === page.page_name ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-bold text-[#2C1810] mb-1">Display Name</label>
                          <input
                            type="text"
                            value={page.display_name}
                            onChange={(e) => updatePageField(index, 'display_name', e.target.value)}
                            className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-[#2C1810] mb-1">Route</label>
                          <input
                            type="text"
                            value={page.route}
                            onChange={(e) => updatePageField(index, 'route', e.target.value)}
                            className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                          />
                        </div>
                      </div>

                      {page.is_password_protected && (
                        <div>
                          <label className="block text-sm font-bold text-[#2C1810] mb-1">Password</label>
                          <input
                            type="password"
                            value={page.password || ''}
                            onChange={(e) => updatePageField(index, 'password', e.target.value)}
                            placeholder="Enter page password"
                            className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                          />
                        </div>
                      )}

                      {/* Ernie.Ai AI Model Configuration */}
                      {page.page_name === 'ernie_ai' && (
                        <div className="border-t-2 border-[#D4A574] pt-3 mt-3">
                          <h4 className="font-bold text-[#2C1810] mb-2 flex items-center">
                            🤖 AI Model Configuration
                          </h4>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-sm font-bold text-[#2C1810] mb-1">Default AI Model</label>
                              <select
                                value={page.default_ai_model || 'gpt-4o'}
                                onChange={(e) => updatePageField(index, 'default_ai_model', e.target.value)}
                                className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                              >
                                <option value="gpt-4o">GPT-4o (OpenAI)</option>
                                <option value="claude-sonnet-4">Claude Sonnet 4 (Anthropic)</option>
                                <option value="gemini-2.0-flash">Gemini 2.0 Flash (Google)</option>
                              </select>
                              <p className="text-xs text-[#6B4423] mt-1">Users can change this in their conversations</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Hero Text Fields */}
                      <div className="border-t-2 border-[#D4A574] pt-3 mt-3">
                        <h4 className="font-bold text-[#2C1810] mb-2">Hero Section Text</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-bold text-[#2C1810] mb-1">Hero Title</label>
                            <input
                              type="text"
                              value={page.hero_title || ''}
                              onChange={(e) => updatePageField(index, 'hero_title', e.target.value)}
                              placeholder="e.g., Testing Center"
                              className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-bold text-[#2C1810] mb-1">Hero Subtitle</label>
                            <input
                              type="text"
                              value={page.hero_subtitle || ''}
                              onChange={(e) => updatePageField(index, 'hero_subtitle', e.target.value)}
                              placeholder="e.g., Prepare for Your Acupuncture Boards"
                              className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-bold text-[#2C1810] mb-1">Hero Description</label>
                            <input
                              type="text"
                              value={page.hero_description || ''}
                              onChange={(e) => updatePageField(index, 'hero_description', e.target.value)}
                              placeholder="e.g., Three Levels of Practice Testing"
                              className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                            />
                          </div>
                        </div>
                      </div>

                      {/* SEO Fields */}
                      <div className="border-t-2 border-[#D4A574] pt-3 mt-3">
                        <h4 className="font-bold text-[#2C1810] mb-2 flex items-center">
                          <Search className="w-4 h-4 mr-2" />
                          SEO Settings
                        </h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-bold text-[#2C1810] mb-1">SEO Title</label>
                            <input
                              type="text"
                              value={page.seo_title || ''}
                              onChange={(e) => updatePageField(index, 'seo_title', e.target.value)}
                              placeholder="e.g., AcuErnie - Traditional Chinese Medicine"
                              className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-bold text-[#2C1810] mb-1">SEO Description</label>
                            <textarea
                              value={page.seo_description || ''}
                              onChange={(e) => updatePageField(index, 'seo_description', e.target.value)}
                              placeholder="Brief description for search engines (150-160 characters)"
                              rows="2"
                              className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-bold text-[#2C1810] mb-1">SEO Keywords</label>
                            <input
                              type="text"
                              value={page.seo_keywords || ''}
                              onChange={(e) => updatePageField(index, 'seo_keywords', e.target.value)}
                              placeholder="acupuncture, TCM, traditional medicine"
                              className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingPage(null)}
                    className="retro-button-small w-full"
                  >
                    Done Editing
                  </button>
                </div>
              ) : (
                // View Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                        className="text-[#2C1810] hover:text-[#E8934D] disabled:opacity-30"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveDown(index)}
                        disabled={index === pages.length - 1}
                        className="text-[#2C1810] hover:text-[#E8934D] disabled:opacity-30"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-[#2C1810]">{page.display_name}</h3>
                      <p className="text-sm text-[#6B4423]">{page.route}</p>
                      {page.seo_title && (
                        <p className="text-xs text-green-700 mt-1">✓ SEO Optimized</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleVisibility(index)}
                        className={`p-2 rounded ${
                          page.is_visible
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-400 text-gray-700'
                        }`}
                        title={page.is_visible ? 'Visible in menu' : 'Hidden from menu'}
                      >
                        {page.is_visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                      </button>

                      <button
                        onClick={() => togglePasswordProtection(index)}
                        className={`p-2 rounded ${
                          page.is_password_protected
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-300 text-gray-700'
                        }`}
                        title={page.is_password_protected ? 'Password protected' : 'Not protected'}
                      >
                        {page.is_password_protected ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
                      </button>

                      <button
                        onClick={() => viewPage(page.route)}
                        className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
                        title="View page (opens in new tab)"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Page
                      </button>

                      <button
                        onClick={() => setEditingPage(page.page_name)}
                        className="retro-button-small"
                      >
                        Edit Details
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSaveAll}
          disabled={loading}
          className="retro-button flex items-center space-x-2"
        >
          <Save className="w-5 h-5" />
          <span>{loading ? 'Saving...' : 'Save All Changes'}</span>
        </button>
      </div>
    </div>
  );
}
